<script setup>
import { computed } from 'vue'
import { podiumClass } from '@/lib/podium'
import { eventTypeStyle } from '@/lib/eventTypes'

/**
 * The cup of a trophy: the shape says *what* was won, the colour *how well*.
 *
 * The shape has two levels. The main prize of an event **is** the event: the cup for the
 * championship, the podium for a lap competition, the flag for a stage — the same icon its chip
 * carries, from the shared mapping (see lib/eventTypes). A classification like the top ten is a
 * medal whatever the event, on the other hand: it is the minor award, and making it wear the
 * competition's own icon would put it on a par with the podium. The prize line under the cup says
 * which one it is.
 *
 * The colour is the podium's gold, silver or bronze; every award below it keeps the plain grey.
 */
const props = defineProps({
  /** Award in the shape built by App\Libraries\EventAwardLib. */
  award: { type: Object, required: true },
  /** Icon size in pixels. */
  size: { type: Number, default: 26 },
})

/** Awards below the podium, keyed by the `awardKey` the server writes (see EventAwardLib). */
const MINOR_AWARDS = { top_10: 'mdi-medal' }

const icon = computed(() => MINOR_AWARDS[props.award.awardKey]
  ?? eventTypeStyle(props.award.event?.eventType).icon)

/** A gold, silver or bronze cup for the podium; every other award keeps the plain grey. */
const colorClass = computed(() => podiumClass(props.award.place) ?? 'award-plain')
</script>

<template>
  <v-icon :size="size" :class="colorClass">{{ icon }}</v-icon>
</template>
